import { getLabeledVariableName, op, SympyBuildError } from "./shared";

import type { SympyBuilder } from "./SympyBuilder";
import type { SemanticExpr } from "../types";
import type { NodeJsonMap } from "@corca/data";

export function parseIntegral(builder: SympyBuilder, node: NodeJsonMap["function"]): SemanticExpr {
  const integrandNode = node.children.find((n) => n.type === "inline");
  if (!integrandNode) {
    throw new SympyBuildError(`Empty integral arguments`);
  }

  const differentialFunc = integrandNode.children.find(
    (n) => n.type === "function" && n.props.key === "function-differential",
  );

  if (!differentialFunc) {
    throw new SympyBuildError(`Missing differential in integral`);
  }

  const differentialInlineNode = differentialFunc.children.find((n) => n.type === "inline");
  if (!differentialInlineNode || differentialInlineNode.children.length !== 1) {
    throw new SympyBuildError(`Invalid differential in integral`);
  }

  const differentialVarNode = differentialInlineNode.children[0];
  if (
    differentialVarNode.type !== "variable" ||
    differentialVarNode.children[0].type !== "symbol"
  ) {
    throw new SympyBuildError(`Invalid differential variable in integral`);
  }
  const differentialVarName = getLabeledVariableName(differentialVarNode) ?? "";

  let varSymbol: string;
  if (!builder.variables.has(differentialVarName)) {
    varSymbol = `var${builder.variables.size}`;
    builder.variables.set(differentialVarName, varSymbol);
  } else {
    varSymbol = builder.variables.get(differentialVarName)!;
  }

  if (!builder.symbolNodes.has(differentialVarName)) {
    builder.symbolNodes.set(differentialVarName, differentialVarNode);
  }

  if (!varSymbol) {
    throw new SympyBuildError(`Invalid differential variable in integral`);
  }

  const integrandChildren = integrandNode.children.filter((n) => n !== differentialFunc);
  const integrand = builder.traverseNode(
    { type: "inline", props: {}, children: integrandChildren },
    2,
  );

  if (integrand === null || builder.emitInline(integrand) === "") {
    throw new SympyBuildError(`Empty integrand in integral`);
  }

  // The bound variable emits `var0` via the variable lookup, just like every other symbol node.
  const varExpr: SemanticExpr = { type: "symbol", name: differentialVarName };

  const symbolNode = node.children.find((n) => n.type === "symbol");
  const topLabelNode = symbolNode?.children.find(
    (n) => n.type === "label" && n.props.position === "right-top",
  );
  const bottomLabelNode = symbolNode?.children.find(
    (n) => n.type === "label" && n.props.position === "right-bottom",
  );
  const upperLimit = topLabelNode?.children.find((n) => n.type === "inline");
  const lowerLimit = bottomLabelNode?.children.find((n) => n.type === "inline");
  const upperExpr = upperLimit && builder.traverseNode(upperLimit, 2);
  const lowerExpr = lowerLimit && builder.traverseNode(lowerLimit, 2);

  // SymPy serializes an Integral's limits as a bare tuple → JSON array; mirror that here (no Tuple op).
  // Half-typed bounds render as nothing, in which case this stays an indefinite integral.
  if (upperExpr && lowerExpr) {
    return op("Integral", [integrand, [varExpr, lowerExpr, upperExpr]]);
  }
  return op("Integral", [integrand, [varExpr]]);
}
